import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import { renderStatusColor } from "utils/statusColor";
import DropdownNew from "utils/dropdown";
import Filter, { ActiveFilter } from "components/__new/common/Filter";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { TableContent } from "components/__new/common/TableContent";
import { DateFormat } from "utils/dateFormat";
import ReactToolTip from 'react-tooltip';
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import processParams from "utils/processParams";
import DialogBox from "utils/dialogBox";
import { ReactComponent as IconActivate } from "assets/images/icons/project-icons/activate.svg";
import { ReactComponent as IconDeactivate } from "assets/images/icons/project-icons/deactivate.svg";
import { ReactComponent as IconTrash } from "assets/images/icons/project-icons/thrash.svg";
import { ReactComponent as IconDotH } from "assets/images/icons/project-icons/dots-v.svg";
import { ReactComponent as IconsTimes } from "assets/images/icons/project-icons/times.svg";
import useMultiSelect from "hooks/useMultiSelect";
import InviteEmployee from "components/__new/employees/InviteEmployee";
import { toast } from "react-toastify";
import * as employeesAction from "store/entities/employees/action";


export const List = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {urlQueryParams} = useUrlQueryParams();
    const { selections, toggleMultiSelectHeader, getMultiSelectBody, clearSelections } = useMultiSelect();
    const { employeesLoading, employees, employeesDownloadLoading, employeesDownload } = useSelector((s) => s.entities.employees); //eslint-disable-line
    const { employeeBulkActivateLoading, employeeBulkActivateSuccess, employeeBulkDeactivateLoading, employeeBulkDeactivateSuccess, employeeBulkArchiveLoading, employeeBulkArchiveSuccess } = useSelector((s) => s.entities.employees);
    
    
    // const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [isOpenInviteEmployee, setIsOpenInviteEmployee] = useState(false);
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });
    const [showBulkActions, setShowBulkActions] = useState({
        activate: false,
        deactivate: false,
        delete: true,
    });


    const getEmployees = () => {
        let params = {...listParams, status: "active&pending"};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

        setListParams(params);        
        dispatch(employeesAction.getAllEmployees(params));
    }

    
    const statusDisplay = (status) => {
        const data = {
            "active": "Enrolled",
        }
        return data[status?.toLowerCase()] ?? status;
    }


    const resendInvite = async (id) => {
        const result = await DialogBox({
			title: "Resend Invite",
			content: "Are you sure you want to resend the On-Demand Pay invite?",
			buttonYes: "Resend Invite",
			buttonNo: "Cancel",
		});
		if (result) {
            const params = {
                employeeId: id,
            }
            dispatch(employeesAction.resendInvite(params));
            toast("Resending invite");
        }
    }
    
    
    const pauseWithdrawals = async (id) => {
        const result = await DialogBox({
			title: "Pause Withdrawals",
			content: "Are you sure you want to pause On-Demand Pay withdrawals?",
			buttonYes: "Pause withdrawals",
            buttonYesClass: "text-white btn-red",
			buttonNo: "No, thanks",
		});
		if (result) {
            // toast("Revoking access");
            const params = {
                type: "instant",
                startDate: null
            }
            dispatch(employeesAction.pauseEmployeeWithdrawal(id)).then(() => {
                getEmployees();
            })
        }
    }
    
    const resumeWithdrawals = async (id) => {
        const result = await DialogBox({
			title: "Resume Withdrawals",
			content: "Are you sure you want to resume On-Demand Pay withdrawals?",
			buttonYes: "Resume withdrawals",
            buttonYesClass: "text-white btn-primary",
			buttonNo: "No, thanks",
		});
		if (result) {
            // toast("Revoking access");
            const params = {
                type: "instant",
                startDate: null
            }
            dispatch(employeesAction.resumeEmployeeWithdrawal(id)).then(() => {
                getEmployees();
            })
        }
    }
    
    const restartEarnings = async (id) => {
        const result = await DialogBox({
			title: "Restart Earnings",
			content: "Are you sure you want to restart On-Demand Pay earnings?",
			buttonYes: "Restart earnings",
            buttonYesClass: "text-white btn-primary",
			buttonNo: "No, thanks",
		});
		if (result) {
            // toast("Revoking access");
            const params = {
                type: "instant",
                startDate: null
            }
            dispatch(employeesAction.resumeEmployeeEarning(id, params)).then(() => {
                getEmployees();
            })
        }
    }
    
    const stopEarnings = async (id) => {
        const result = await DialogBox({
			title: "Stop Earnings",
			content: "Are you sure you want to stop On-Demand Pay earnings?",
			buttonYes: "Stop earnings",
            buttonYesClass: "text-white btn-red",
			buttonNo: "No, thanks",
		});
		if (result) {
            // toast("Revoking access");
            const params = {
                type: "instant",
                startDate: null
            }
            dispatch(employeesAction.stopEmployeeEarning(id, params)).then(() => {
                getEmployees();
            })
        }
    }
    
    const revokeAccess = async (id) => {
        const result = await DialogBox({
			title: "Revoke Access",
			content: "Are you sure you want to revoke this employee's access to On-Demand Pay?",
			buttonYes: "Revoke",
            buttonYesClass: "text-white btn-red",
			buttonNo: "No, thanks",
		});
		if (result) {
            // toast("Revoking access");
            const params = {
                type: "instant",
                startDate: null
            }
            // dispatch(employeesAction.stopEmployeeEarning(id, params)).then(() => {
            dispatch(employeesAction.deactivateEmployee(id)).then(() => {
                getEmployees();
            })
        }
    }
    
    
    const restoreAccess = async (id) => {
        const result = await DialogBox({
			title: "Restore Access",
			content: "Are you sure you want to restore this employee's access to On-Demand Pay?",
			buttonYes: "Restore Access",
			buttonNo: "Cancel",
		});
		if (result) {
            // toast("Restoring access");
            dispatch(employeesAction.resumeEmployeeEarning(id)).then(() => {
                getEmployees();
            })
        }
    }
    

    const processBulkActionOptions = () => {
        let bulkActions = {
            activate: false,
            deactivate: false,
            delete: true,
        };
        selections?.filter((selection) => {
            const employee = JSON.parse(selection);
            if (employee.disabled){
                bulkActions = {...bulkActions, activate: true};
            }
            if (!employee.disabled){
                bulkActions = {...bulkActions, deactivate: true};
            }
            return null;
        });
        setShowBulkActions(bulkActions);
    }


    const bulkActivateEmployees = async () => {
        const result = await DialogBox({
			title: "Activate Employees",
			content: "Are you sure you want to activate the selected employees? The employees will have access to Earnipay",
			buttonYes: "Activate",
			buttonNo: "Cancel",
		});
		if (result) {
            const employeeIds = selections.map((selection) => JSON.parse(selection)?._id);
            const params = {employeeIds}
            dispatch(employeesAction.bulkActivateEmployees(params));
        }
    }


    const bulkDeactivateEmployees = async () => {
        const result = await DialogBox({
			title: "Deactivate Employees",
			content: "Are you sure you want to deactivate the selected employees? Employees will no longer have access to Earnipay",
			buttonYes: "Deactivate",
            buttonYesClass: "text-white btn-red",
			buttonNo: "No, thanks",
		});
		if (result) {
            const employeeIds = selections.map((selection) => JSON.parse(selection)?._id);
            const params = {employeeIds}
            dispatch(employeesAction.bulkDeactivateEmployees(params));
        }
    }


    const bulkArchiveEmployees = async () => {
        const result = await DialogBox({
			title: "Delete Employees",
			content: "Are you sure you want to delete the selected employees? Details of these employees will be erased if you choose to continue.",
			buttonYes: "Delete Employees",
			buttonYesClass: "text-white btn-red",
			buttonNo: "No, thanks",
		});
		if (result) {
            const employeeIds = selections.map((selection) => JSON.parse(selection)?._id);
            const params = {employeeIds}
            dispatch(employeesAction.bulkArchiveEmployees(params));
        }
    }


    useEffect(() => {
        getEmployees();
        // eslint-disable-next-line
    }, [location]);


    useEffect(() => {
        processBulkActionOptions();
        // eslint-disable-next-line
    }, [selections]);


    useEffect(() => {
        if (employeeBulkActivateSuccess === true || employeeBulkDeactivateSuccess === true || employeeBulkArchiveSuccess === true){
            getEmployees();
            clearSelections();
        }
        // eslint-disable-next-line
    }, [employeeBulkActivateSuccess, employeeBulkDeactivateSuccess, employeeBulkArchiveSuccess]);


    return (
        <div>


            <DynamicHeadTag
                headerText="Invited Employees"
                headerDescription="Manage and monitor your employees' payment activity."
            />


            <InviteEmployee
                isOpen={isOpenInviteEmployee}
                setIsOpen={setIsOpenInviteEmployee}
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        Invited Employees
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>


            <div className="mt-6 box !px-0 !pb-0">
                <div className="px-4 font-medium text-xl">
                    Employees
                </div>

                <div className={`px-4 mt-6 xl:flex flex-wrap ${selections?.length > 0 ? "justify-start" : "justify-between"}`}>
                    <div className="flex-grow flex items-center">
                        {isOpenInviteEmployee === false &&
                            <div className="flex">
                                <input type="checkbox" className="form-checkbox w-5 h-5 multi-select-header" onChange={toggleMultiSelectHeader} />
                            </div>
                        }
                        <div className="w-10 flex justify-center items-center">
                            <IconDotH className="h-6 svg-fill-ccc" />
                        </div>
                        <Maybe condition={selections?.length > 0}>
                            <div className="flex flex-wrap items-center">

                                <Maybe condition={showBulkActions?.activate === true}>
                                    <button type="button" onClick={bulkActivateEmployees} className="dropdown-item dropdown-item-icon" disabled={employeeBulkActivateLoading}>
                                        <Maybe condition={employeeBulkActivateLoading === true}>
                                            <FontAwesomeIcon icon="circle-notch" spin className="text-xl" style={{margin: "auto 0.5rem auto 0"}} />
                                        </Maybe>
                                        <Maybe condition={employeeBulkActivateLoading === false}>
                                            <IconActivate style={{height: "1.25rem", marginRight: "0.5rem"}} />
                                        </Maybe>
                                        Activate
                                    </button>
                                </Maybe>

                                <Maybe condition={showBulkActions?.deactivate === true}>
                                    <button type="button" onClick={bulkDeactivateEmployees} className="dropdown-item dropdown-item-icon" disabled={employeeBulkDeactivateLoading}>
                                        <Maybe condition={employeeBulkDeactivateLoading === true}>
                                            <FontAwesomeIcon icon="circle-notch" spin className="text-xl" style={{margin: "auto 0.5rem auto 0"}} />
                                        </Maybe>
                                        <Maybe condition={employeeBulkDeactivateLoading === false}>
                                            <IconDeactivate style={{height: "1.25rem", marginRight: "0.5rem"}} />
                                        </Maybe>
                                        Deactivate
                                    </button>
                                </Maybe>
                                
                                <Maybe condition={showBulkActions?.delete === true}>
                                    <button type="button" onClick={bulkArchiveEmployees} className="dropdown-item dropdown-item-icon text-red-500" disabled={employeeBulkArchiveLoading}>
                                        <Maybe condition={employeeBulkArchiveLoading === true}>
                                            <FontAwesomeIcon icon="circle-notch" spin className="text-xl" style={{margin: "auto 0.5rem auto 0"}} />
                                        </Maybe>
                                        <Maybe condition={employeeBulkArchiveLoading === false}>
                                            <IconTrash style={{height: "1.25rem", marginRight: "0.5rem"}} />
                                        </Maybe>
                                        Delete
                                    </button>
                                </Maybe>

                                <div className="mx-2 sm:mx-6">
                                    <div className="w-0.5 h-4 bg-gray-200"></div>
                                </div>

                                <div className="flex space-x-4">
                                    <div className="text-[#6C757D]">
                                        {selections?.length} selected
                                    </div>
                                    <button type="button" onClick={clearSelections}>
                                        <IconsTimes className="svg-fill-red" />
                                    </button>
                                </div>
                            </div>
                        </Maybe>
                        <Maybe condition={selections?.length === 0}>
                            <div className="w-full max-w-sm sm:mr-6">
                                <SearchBox placeholder="Search Employee..." name="search" extraUrlParams="page=1" />
                            </div>
                        </Maybe>
                    </div>
                    <Maybe condition={selections?.length === 0}>
                        <div className="mt-2 xl:mt-0 flex flex-wrap space-x-3 sm:space-x-6">

                            <div>
                                <Filter dropdownClass="dropdown-right" filters={[
                                    {
                                        title: "Date Added",
                                        dataType: "date-range",
                                        options: {
                                            from: {
                                                name: "startDate",
                                                placeholder: "Start Date",
                                            },
                                            to: {
                                                name: "endDate",
                                                placeholder: "End Date",
                                            },
                                        },
                                    },
                                ]}/>
                            </div>

                            {/* 
                            <div>
                                <DownloadAsCSV
                                    ref={downloadAsCSVRef}
                                    tooltip={`Download invited employees list`}
                                    filename="Invited Employees.csv"
                                    columns={[
                                        { label: "Employee ID", key: "employeeId" },
                                        { label: "Full Name", key: "fullName" },
                                        { label: "Email Address", key: "email" },
                                        { label: "Phone Number", key: "phoneNumber" },
                                        { label: "Net Monthly Salary", key: "netMonthlySalary" },
                                        { label: "Bank Name", key: "bankName" },
                                        { label: "Account Number", key: "bankAccountNumber" },
                                        { label: "Job Title", key: "jobTitle" },
                                        { label: "Group", key: "employeeGroup" },
                                        { label: "Date Added", key: "createdAt" },
                                        { label: "Status", key: "status" },
                                        { label: "Disabed", key: "disabled" },
                                    ]}
                                    click={() => {
                                        dispatch(employeesAction.downloadGetAllEmployees({...listParams, page: 1, limit: 10000})).then(() => {
                                            downloadAsCSVRef.current?.downloadData();
                                        });
                                    }}
                                    loading={employeesDownloadLoading}
                                    response={employeesDownload?.employees?.docs}
                                />
                            </div>

                            <div>
                                <Print component={printComponentRef} />
                            </div>
                            */}
                            
                            <div>
                                <button type="button" onClick={() => setIsOpenInviteEmployee(true)} className="btn btn-md btn-ep-primary">
                                    <FontAwesomeIcon icon="plus" className="mr-2" />
                                    Invite Employee
                                </button>
                            </div>

                        </div>
                    </Maybe>
                </div>

                <div>
                    <ActiveFilter filters={{"Status": "status", "Start Date": "startDate", "End Date": "endDate"}} />
                </div>
                
                
                <div className="mt-6">
                    <div ref={printComponentRef}>

                        <div className="mb-4 text-xl hidden print:block">
                            Employees
                        </div>
                        
                        <Table>
                            <Table.Head>
                                <th></th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Phone Number</th>
                                <th>Role</th>
                                <th>Date Created</th>
                                <th>Status</th>
                                <th></th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={employeesLoading}
                                    loadingText={`Loading invited employees`}
                                    data={employees?.employees}
                                    colspan={8}
                                />

                                {employeesLoading === false && employees?.employees?.docs?.map((employee) =>
                                    <tr key={employee._id}>
                                        <td>
                                            {isOpenInviteEmployee === false &&
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox w-5 h-5 multi-select"
                                                    onChange={getMultiSelectBody}
                                                    checked={selections?.filter((selection) => JSON.parse(selection)._id === employee._id).length === 1}
                                                    value={JSON.stringify(employee)}
                                                />
                                            }
                                        </td>
                                        <td>
                                            <div className="flex space-x-1">
                                                <div className="capitalize">
                                                    {employee.firstName}
                                                </div>
                                                <Maybe condition={employee.stopEarning === true || employee.stopEarningFrom !== null}>
                                                    <div>
                                                        <FontAwesomeIcon data-tip data-for={"info-stop-earnings-" + employee._id} icon="info-circle" className="text-red-500 text-lg" />
                                                        <ReactToolTip id={"info-stop-earnings-" + employee._id} className="tooltip">
                                                            <Maybe condition={employee.stopEarning === true}>
                                                                <div className="label label-red">
                                                                    Earnings OFF
                                                                </div>
                                                            </Maybe>
                                                            <Maybe condition={employee.stopEarning === false && employee.stopEarningFrom !== null}>
                                                                <div className="label label-red">
                                                                    Earnings OFF (scheduled)
                                                                </div>
                                                            </Maybe>
                                                            <div className="mt-2 text-white text-center">
                                                                <Maybe condition={employee.stopEarningFrom !== null}>
                                                                    {DateFormat(employee.stopEarningFrom)} -&nbsp;
                                                                    {(employee.stopEarningTo !== null) ? DateFormat(employee.stopEarningTo) : "infinity" }
                                                                </Maybe>
                                                            </div>
                                                        </ReactToolTip>
                                                    </div>
                                                </Maybe>
                                            </div>
                                        </td>
                                        <td className="capitalize">
                                            {employee?.lastName}
                                        </td>
                                        <td>
                                            {employee.email}
                                        </td>
                                        <td>
                                            {employee.phoneNumber}
                                        </td>
                                        <td>
                                            {employee.jobTitle}
                                        </td>
                                        <td>
                                            {DateFormat(employee.createdAt)}
                                        </td>
                                        <td>
                                            <div className={"label capitalize " + renderStatusColor(employee.status)}>
                                                {statusDisplay(employee?.status) ?? "-"}
                                            </div>
                                        </td>
                                        <td>
                                            <DropdownNew flipSwitch={true} >
                                                <></>
                                                <div>
                                                    <Maybe condition={employee.status !== "active"}>
                                                        <div onClick={() => resendInvite(employee._id)} className="dropdown-item dropdown-item-icon">
                                                            Resend On-Demand Pay Invite
                                                        </div>
                                                    </Maybe>
                                                    <Maybe condition={(employee.accessGranted === false)  && (employee.disabled === true)  && (employee.status !== "pending")}>
                                                        <div onClick={() => resumeWithdrawals(employee._id)} className="dropdown-item dropdown-item-icon">
                                                            Resume Withdrawals
                                                        </div>
                                                    </Maybe>
                                                    <Maybe condition={(employee.accessGranted === true)  && (employee.disabled !== true)  && (employee.status !== "pending")}>
                                                        <div onClick={() => pauseWithdrawals(employee._id)} className="dropdown-item dropdown-item-icon">
                                                            Pause Withdrawals
                                                        </div>
                                                    </Maybe>
                                                    <Maybe condition={(employee.stopEarning === true)  && (employee.status !== "pending")}>
                                                        <div onClick={() => restartEarnings(employee._id)} className="dropdown-item dropdown-item-icon">
                                                            Restart Earnings
                                                        </div>
                                                    </Maybe>
                                                    <Maybe condition={(employee.stopEarning !== true)  && (employee.status !== "pending")}>
                                                        <div onClick={() => stopEarnings(employee._id)} className="dropdown-item dropdown-item-icon">
                                                            Stop Earnings
                                                        </div>
                                                        <div onClick={() => revokeAccess(employee._id)} className="dropdown-item dropdown-item-icon text-red-500">
                                                            Revoke Access To On-Demand Pay
                                                        </div>
                                                    </Maybe>
                                                    <Maybe condition={employee.stopEarning === true && (employee.status !== "pending")}>
                                                        <div onClick={() => restoreAccess(employee._id)} className="dropdown-item dropdown-item-icon">
                                                            Restore Access To On-Demand Pay
                                                        </div>
                                                    </Maybe>
                                                </div>
                                            </DropdownNew>
                                        </td>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={employeesLoading === false && employees?.employees?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={employees?.employees} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>

        </div>
    )
}


export default List;